import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../Breadcrumb";
import { fetchUserData } from "../../actions/user";
import CommonView from "./CommonView";
import PatternKeisouView from "./PatternKeisouView";
import PatternSystemView from "./PatternSystemView";
import PatternKeisokuView from "./PatternKeisokuView";
import { fetchMenuData } from "../../actions/sidemenu";
import axiosInstance from "../../helpers/refreshTokenMiddleware";
import BASE_URL from "../../constants/Api";
import "./DocumentView.css";
import "../DocumentRegister/DocumentRegister.css";
import Modal from "../Modal/Modal";

const sortByDisplayIdx = (list = []) =>
  [...list].sort((a, b) => (a?.display_idx || 0) - (b?.display_idx || 0));

const inferPatternFromFolderName = (name = "") => {
  if (!name) return "DEFAULT";

  if (name.includes("E版")) return "E";
  if (name.includes("T版")) return "T";
  if (name.includes("D版")) return "D";
  if (name.includes("C版")) return "C";
  if (name.includes("B版")) return "B";

  if (name.includes("EM版")) return "EM";
  if (name.includes("TM版")) return "TM";
  if (name.includes("DM版")) return "DM";
  if (name.includes("CM版")) return "CM";
  if (name.includes("BM版")) return "BM";
  if (name.includes("図面表")) return "ZM";
  if (name.includes("購入品リスト")) return "KM";

  if (name.includes("EK版")) return "EK";
  if (name.includes("TK版")) return "TK";
  if (name.includes("DK版")) return "DK";
  if (name.includes("CK版")) return "CK";

  return "DEFAULT";
};

const formatDate = (value) => {
  if (!value) return "";

  const [datePart] = value.split(" ");
  const delimiter = datePart.includes("/") ? "/" : "-";
  const [year, month, day] = datePart.split(delimiter);

  if (!year || !month || !day) {
    return value;
  }

  const paddedMonth = String(month).padStart(2, "0");
  const paddedDay = String(day).padStart(2, "0");
  return `${year}年${paddedMonth}月${paddedDay}日`;
};

const formatDateTime = (value) => {
  if (!value) return "";

  const [datePart, timePart] = value.split(" ");
  const dateFormatted = formatDate(datePart);

  if (!timePart) {
    return dateFormatted;
  }

  const [hour = "00", minute = "00", second = "00"] = timePart.split(":");
  return `${dateFormatted} ${hour}時${minute}分${second}秒`;
};

const findCategoryText = (categoryList, id) => {
  if (id === undefined || id === null || id === "") return "";
  const numericId = Number(id);
  const found = categoryList.find(
    (item) => Number(item.document_category) === numericId
  );
  return found?.document_category_text || "";
};

const findTypeText = (typeList, id) => {
  if (id === undefined || id === null || id === "") return "";
  const numericId = Number(id);
  const found = typeList.find(
    (item) => Number(item.document_type) === numericId
  );
  return found?.document_type_text || "";
};

const DocumentViewBase = ({ user, menuData }) => {
  const { docId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState("");
  const [viewLoading, setViewLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyFinished, setCopyFinished] = useState(false); // コピー完了モーダル表示フラグ
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false); // コピー確認モーダル

  const [deptId, setDeptId] = useState(null);
  const [folderId, setFolderId] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [departmentName, setDepartmentName] = useState("");

  const [patternMaster, setPatternMaster] = useState({
    document_category: [],
    document_type: [],
  });

  const [masterError, setMasterError] = useState("");

  const token = localStorage.getItem("accessToken");
  useEffect(() => {
    if (token) {
      dispatch(fetchUserData());
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (!menuData || menuData.length === 0) {
      dispatch(fetchMenuData());
    }
  }, [dispatch, menuData]);

  const userName =
    user?.user_name || user?.name || user?.username || user?.displayName || "";

  useEffect(() => {
    if (!deptId || !folderId) {
      setPatternMaster({
        document_category: [],
        document_type: [],
      });
      return;
    }

    const fetchPatternMaster = async () => {
      try {
        setMasterError("");
        const response = await axiosInstance.post(`${BASE_URL}/top/data-new`, {
          department_id: deptId,
          folder_id: folderId,
        });

        const payload = response.data?.respons || {};
        setPatternMaster({
          document_category: sortByDisplayIdx(payload.document_category || []),
          document_type: sortByDisplayIdx(payload.document_type || []),
        });
      } catch (error) {
        console.error("Failed to fetch pattern master", error);
        setMasterError("図面種別情報を取得できませんでした。");
        setPatternMaster({
          document_category: [],
          document_type: [],
        });
      }
    };

    fetchPatternMaster();
  }, [deptId, folderId]);

  useEffect(() => {
    if (!docId) {
      setViewData(null);
      setViewError("文書を特定できませんでした。");
      return;
    }

    const fetchDocument = async () => {
      try {
        setViewLoading(true);
        setViewError("");
        const response = await axiosInstance.post(`${BASE_URL}/top/dataview`, {
          doc_id: docId,
        });

        if (response?.data?.status !== 200) {
          throw new Error(
            response?.data?.respons?.message || "文書情報の取得に失敗しました。"
          );
        }

        const payload = response.data?.respons || {};
        setViewData(payload);

        if (payload.department_id) {
          setDeptId(payload.department_id);
        }
        if (payload.folder_id) {
          setFolderId(payload.folder_id);
        }
        if (payload.folder_name) {
          setFolderName(payload.folder_name);
        }
        if (payload.department_name) {
          setDepartmentName(payload.department_name);
        }
      } catch (error) {
        console.error("文書情報の取得に失敗しました。", error);
        setViewError(
          error?.message ||
            "文書情報の取得に失敗しました。もう一度お試しください。"
        );
      } finally {
        setViewLoading(false);
      }
    };

    fetchDocument();
  }, [docId]);

  const handleConfirm = () => {
    window.close();
  };

  const handleCopy = async () => {
    try {
      setIsConfirmModalOpen(false);
      setCopyLoading(true);
      setViewError("");
      const response = await axiosInstance.post(`${BASE_URL}/top/data-copy`, {
        doc_id: docId,
      });

      if (response?.data?.status !== 200) {
        throw new Error(
          response?.data?.respons?.message || "文書コピーに失敗しました。"
        );
      }

      // const payload = response.data?.respons || {};
      // setViewData(payload);
      setCopyFinished(true);
      // window.close();
    } catch (error) {
      console.error("文書コピーに失敗しました。", error);
      setViewError("文書コピーに失敗しました。もう一度お試しください。");
    } finally {
      setCopyLoading(false);
    }
    
  };

  const prefix = inferPatternFromFolderName(folderName);
  const autoNumber = useMemo(() => {
    if (!viewData?.auto_number) return "";
    if (!prefix || prefix === "DEFAULT") {
      return String(viewData.auto_number);
    }
    return `${prefix}-${viewData.auto_number}`;
  }, [viewData?.auto_number, prefix]);

  const registerDateText = formatDateTime(viewData?.u_datetime);
  const issueDateText = formatDate(viewData?.issue_datetime);
  const registrant = viewData?.user_name || "";
  const assignee = viewData?.assignee || "";
  const viewCountText =
    viewData?.view_count !== undefined && viewData?.view_count !== null
      ? `${viewData.view_count}回`
      : "";

  const documentCategoryText = findCategoryText(
    patternMaster.document_category,
    viewData?.document_category
  );
  const documentTypeText = findTypeText(
    patternMaster.document_type,
    viewData?.document_type
  );

  const patternContent = useMemo(() => {
    if (!viewData) return null;

    if (deptId === "D001") {
      return (
        <PatternKeisouView
          documentCategoryText={documentCategoryText}
          documentTypeText={documentTypeText}
          productNo={viewData.product_no || ""}
          productModel={viewData.product_model || ""}
          destination={viewData.destination || ""}
          customer={viewData.customer || ""}
          varietyName={viewData.variety_name || ""}
          issueDate={issueDateText}
          assignee={assignee}
          remarks={viewData.remarks || ""}
        />
      );
    }

    if (deptId === "D002") {
      return (
        <PatternSystemView
          documentCategoryText={documentCategoryText}
          productNo={viewData.product_no || ""}
          destination={viewData.destination || ""}
          customer={viewData.customer || ""}
          issueDate={issueDateText}
          assignee={assignee}
          deviceName={viewData.device_name || ""}
          assemblyType={viewData.assembly_type || ""}
          remarks={viewData.remarks || ""}
        />
      );
    }

    if (deptId === "D003") {
      return (
        <PatternKeisokuView
          documentCategoryText={documentCategoryText}
          documentTypeText={documentTypeText}
          issueDate={issueDateText}
          categoryName={viewData.category_name || ""}
          assignee={assignee}
          productName={viewData.product_name || ""}
          productModel={viewData.product_model || ""}
          originalDocNumber={viewData.original_doc_number || ""}
          purpose={viewData.purpose || ""}
          customer={viewData.customer || ""}
          remarks={viewData.remarks || ""}
        />
      );
    }

    return null;
  }, [
    assignee,
    deptId,
    documentCategoryText,
    documentTypeText,
    issueDateText,
    viewData,
  ]);

  return (
    <div className="contents">
      <Breadcrumb
        userName={userName}
        departmentName={departmentName}
        folderName={folderName}
        deptId={deptId}
        folderId={folderId}
      />

      <div className="document-view-container">
        <h1 className="document-view-title">文書参照</h1>

        {viewLoading && (
          <div className="document-view__loading">読込中です…</div>
        )}

        {copyLoading && (
          <div className="document-view__loading">コピー中です…</div>
        )}

        <Modal
          isOpen={copyFinished}
          onClose={() => window.close()}
          message={"コピーが完了しました。\n画面を更新して最新の情報をご確認ください。"}
        />

        <Modal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleCopy}
          message={"以下タイトルの文書をコピーします。\nタイトル： " + (viewData?.title || "無題")}
          showCancelButton={true}
        />

        {viewError && !viewLoading && !copyLoading && (
          <div className="document-view__error">{viewError}</div>
        )}

        {masterError && !viewLoading && !copyLoading && (
          <div className="document-view__error">{masterError}</div>
        )}

        {viewData && !viewLoading && !copyLoading && (
          <>
            <CommonView
              folderName={folderName}
              title={viewData.title || folderName || ""}
              autoNumber={autoNumber}
              registerDate={registerDateText}
              registrant={registrant}
              viewCount={viewCountText}
              onEdit={() => navigate(`/viewId/${docId}/edit`)}
              onCopy={() => setIsConfirmModalOpen(true)}
            />

            {patternContent && (
              <div className="pattern-section">{patternContent}</div>
            )}

            {/* ボタングループ */}
            <div className="button-group view-button-group">
              <button
                type="button"
                className="btn view-confirm-btn"
                onClick={handleConfirm}
              >
                確認
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

DocumentViewBase.propTypes = {
  user: PropTypes.shape({
    user_name: PropTypes.string,
    name: PropTypes.string,
    username: PropTypes.string,
    displayName: PropTypes.string,
  }),
  menuData: PropTypes.arrayOf(
    PropTypes.shape({
      department_id: PropTypes.string,
      // department_name: PropTypes.string,
      folder: PropTypes.arrayOf(
        PropTypes.shape({
          folder_id: PropTypes.string,
          folder_name: PropTypes.string,
        })
      ),
    })
  ),
};

DocumentViewBase.defaultProps = {
  user: null,
  menuData: [],
};

export default DocumentViewBase;
